"use client";

// FILE: src/shared/components/cookie-banner.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Компонент уведомления об использовании cookie с двумя вариантами отображения: CRM (корпоративный, сине-белый) и Public/Portal (тёмный брутальный с зелёным акцентом).
// SCOPE: Определение публичной страницы (isPublicPage), чтение/запись localStorage для oxpa_cookie_consent, два визуальных варианта баннера.
// INPUT: pathname из usePathname().
// OUTPUT: React-компонент баннера (или null если согласие уже дано или страница непубличная).
// KEYWORDS: DOMAIN(Compliance): CookieConsent; DOMAIN(Privacy): GDPR; TECH(React): ClientComponent
// END_MODULE_CONTRACT
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.1.0 — BRAND v1.0: публичный вариант на токенах --portal-* (зелёный -> янтарь, radius 0, хард-тень); расширен список публичных префиксов (/tools, /login, /guard, /holdings и др.) — раньше там показывался CRM-вариант (синий)
// PREV_CHANGE_SUMMARY: 1.0.0 — Добавлена семантическая разметка (Ponytail audit: ступень 6)
// END_CHANGE_SUMMARY
//
// START_MODULE_MAP:
// FUNC [10] Компонент баннера cookie с двумя вариантами => CookieBanner
// FUNC [3] Проверка, является ли страница публичной => isPublicPage
// END_MODULE_MAP

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Cookie, X } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import Link from "next/link";

const PUBLIC_PATH_PREFIXES = [
    "/rating", "/wiki", "/calc", "/uslugi", "/chop", "/claim", "/ref",
    "/tools", "/holdings", "/blog", "/login", "/register", "/pdpn",
    "/guard", "/guard-portal", "/partners", "/cabinet", "/contacts",
    "/offer", "/terms", "/fz-427", "/map", "/questionnaire", "/e",
];

function isPublicPage(pathname: string): boolean {
    if (pathname === "/" || pathname === "/consent" || pathname === "/privacy") return true;
    return PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function CookieBanner() {
    const pathname = usePathname();
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        const consent = localStorage.getItem("oxpa_cookie_consent");
        if (!consent) {
            setShowBanner(true);
        }
    }, []);

    const acceptCookies = () => {
        localStorage.setItem("oxpa_cookie_consent", "true");
        setShowBanner(false);
        // BUG_FIX_CONTEXT: Метрика не начинает сбор до согласия. После сохранения consent
        // уведомляем MetrikaConsent через кастомное событие для отложенной инициализации ym("init").
        window.dispatchEvent(new CustomEvent("oxpa:cookie-consent"));
        // 152-ФЗ: логируем факт согласия на cookie (implied consent через конклюдентные действия)
        fetch("/api/pdpn/consent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ source: "cookie_banner" }),
        }).catch(() => {
            // silent — не блокируем UI при ошибке логирования
        });
    };

    const declineCookies = () => {
        // MEMENTO: true compliance requires disabling non-essential cookies, not just hiding the banner
        setShowBanner(false);
    };

    if (!showBanner) return null;

    if (!isPublicPage(pathname)) {
        // CRM / Dashboard variant — corporate blue-white palette
        return (
            <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[420px] bg-background border rounded-lg shadow-lg p-5 z-[100] animate-in slide-in-from-bottom-5 fade-in duration-300">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-3">
                        <Cookie className="w-6 h-6 text-primary mt-0.5 shrink-0" />
                        <div className="flex flex-col gap-2">
                            <h3 className="font-semibold text-sm">
                                Мы используем файлы cookie
                            </h3>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                                Продолжая работу с сайтом, вы даете{" "}
                                <Link
                                    href="/consent"
                                    className="underline hover:text-foreground font-medium"
                                >
                                    согласие на обработку персональных данных
                                </Link>{" "}
                                и использование файлов cookie в соответствии с{" "}
                                <Link
                                    href="/privacy"
                                    className="underline hover:text-foreground font-medium"
                                >
                                    Политикой конфиденциальности
                                </Link>.
                            </p>
                            <div className="flex items-center gap-2 mt-3">
                                <Button
                                    onClick={acceptCookies}
                                    size="sm"
                                    className="w-full sm:w-auto font-medium"
                                >
                                    Принять
                                </Button>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={declineCookies}
                        className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                        aria-label="Скрыть уведомление"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    // Public / Portal variant — тёмная BRAND-тема на токенах --portal-* (янтарный акцент)
    return (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[420px] bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] rounded-none shadow-[4px_4px_0_hsl(var(--portal-bg))] p-5 z-[100] animate-in slide-in-from-bottom-5 fade-in duration-300 font-mono">
            <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3">
                    <Cookie className="w-6 h-6 text-[hsl(var(--portal-accent))] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <div className="flex flex-col gap-2">
                        <h3 className="font-semibold text-sm text-[hsl(var(--portal-text))]">
                            Мы используем файлы cookie
                        </h3>
                        <p className="text-xs text-[hsl(var(--portal-text-muted))] leading-relaxed">
                            Продолжая работу с сайтом, вы даете{" "}
                            <Link
                                href="/consent"
                                className="underline text-[hsl(var(--portal-text-secondary))] hover:text-[hsl(var(--portal-accent))] font-medium transition-colors"
                            >
                                согласие на обработку персональных данных
                            </Link>{" "}
                            и использование файлов cookie в соответствии с{" "}
                            <Link
                                href="/privacy"
                                className="underline text-[hsl(var(--portal-text-secondary))] hover:text-[hsl(var(--portal-accent))] font-medium transition-colors"
                            >
                                Политикой конфиденциальности
                            </Link>.
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                            <Button
                                onClick={acceptCookies}
                                size="sm"
                                className="w-full sm:w-auto font-medium rounded-none uppercase tracking-widest text-[10px] bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))]"
                            >
                                Принять
                            </Button>
                        </div>
                    </div>
                </div>
                <button
                    onClick={declineCookies}
                    className="text-[hsl(var(--portal-text-dim))] hover:text-[hsl(var(--portal-text))] transition-colors shrink-0"
                    aria-label="Скрыть уведомление"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
